import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { X, Sparkles, CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'doctorite_popup_dismissed_at';
const SUPPRESS_DAYS = 7;
const MOBILE_TIMER_MS = 30_000;
const SCROLL_TRIGGER_PCT = 0.5;

interface PopupFormData {
  name: string;
  email: string;
  practice: string;
}

function shouldSuppress(): boolean {
  try {
    const ts = localStorage.getItem(STORAGE_KEY);
    if (!ts) return false;
    const dismissedAt = parseInt(ts, 10);
    if (Number.isNaN(dismissedAt)) return false;
    const ageDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return ageDays < SUPPRESS_DAYS;
  } catch {
    return false;
  }
}

function recordDismissal() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable — silently no-op; popup will reappear next visit
  }
}

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
}

export default function LeadCapturePopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const hasTriggered = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PopupFormData>();

  useEffect(() => {
    if (shouldSuppress()) return;

    const trigger = () => {
      if (hasTriggered.current) return;
      hasTriggered.current = true;
      setIsOpen(true);
    };

    // Desktop: exit-intent (mouse leaving viewport at top)
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !isMobile()) trigger();
    };

    // Mobile: timer fallback
    const timerId = isMobile() ? window.setTimeout(trigger, MOBILE_TIMER_MS) : null;

    // Mobile: scroll-depth fallback
    const handleScroll = () => {
      if (!isMobile()) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total >= SCROLL_TRIGGER_PCT) trigger();
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, []);

  const close = () => {
    setIsOpen(false);
    recordDismissal();
  };

  const onSubmit = async (data: PopupFormData) => {
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      if (serviceId && templateId && publicKey) {
        await emailjs.send(
          serviceId,
          templateId,
          {
            first_name: data.name,
            last_name: '(popup)',
            email: data.email,
            phone: 'Not provided',
            practice_size: data.practice || 'Not specified',
            current_platform: 'Not specified',
            message: 'Lead from exit-intent popup',
          },
          { publicKey }
        );
      }
      setIsSubmitted(true);
      recordDismissal();
    } catch (err) {
      // Don't crash the user — log and let them try again
      console.error('Lead popup submission failed:', err);
      alert('Something went wrong. Please email hello@doctorite.ai and we’ll set up your demo.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
            aria-hidden="true"
          />

          {/* Modal (desktop) / Bottom sheet (mobile) */}
          <div className="fixed inset-0 z-[101] flex items-end justify-center sm:items-center sm:p-4 pointer-events-none">
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-title"
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 48 }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            className="pointer-events-auto w-full sm:max-w-lg"
          >
            <div className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="relative bg-gradient-to-br from-brand-600 to-brand-800 px-6 sm:px-8 py-7 text-white">
                <button
                  onClick={close}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/15 backdrop-blur-sm mb-3">
                  <Sparkles className="w-3.5 h-3.5" />
                  Wait — before you go
                </div>
                <h2 id="popup-title" className="text-2xl sm:text-3xl font-bold tracking-tight">
                  See Doctorite in action.
                </h2>
                <p className="mt-2 text-brand-100 text-sm sm:text-base leading-relaxed">
                  Book a 20-minute demo and we’ll show you a real session, real notes, real billing — start to finish.
                </p>
              </div>

              {/* Body */}
              <div className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">You’re on the list.</h3>
                    <p className="text-sm text-gray-600 mb-5">
                      We’ll be in touch within one business day to confirm your demo time.
                    </p>
                    <button onClick={close} className="btn-secondary">
                      Keep browsing
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                      <label htmlFor="popup-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Your name
                      </label>
                      <input
                        id="popup-name"
                        type="text"
                        {...register('name', { required: 'Name is required' })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                        placeholder="Jane Smith"
                      />
                      {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="popup-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Work email
                      </label>
                      <input
                        id="popup-email"
                        type="email"
                        {...register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                        })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                        placeholder="jane@practice.com"
                      />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="popup-practice" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Practice type
                      </label>
                      <select
                        id="popup-practice"
                        {...register('practice')}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                      >
                        <option value="">Select…</option>
                        <option value="solo">Solo therapist / counselor</option>
                        <option value="clinic">Multi-provider clinic</option>
                        <option value="group">Group practice / organization</option>
                        <option value="care-management">Care management</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full !py-3.5 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Sending…
                        </span>
                      ) : (
                        <>
                          Book my free demo
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>

                    <p className="flex items-center justify-center gap-2 text-xs text-gray-500 pt-1">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      No spam. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
